'use client';

import React, { useState, useContext } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { MessageCircle, Award, BookOpen, Edit3 } from 'lucide-react';
import VideoModal from './VideoModal';
import UploadIntroVidModal from '@components/settings/modals/UploadIntroVidModal';
import { UserContext } from '@/context/UserContext';

export default function TeacherHeroSection({ profileData, onMessageClick }) {
  const router = useRouter();
  const ctx = useContext(UserContext);

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [uploadVidModalOpen, setUploadVidModalOpen] = useState(false);
  // track avatar load failure (prevents fullscreen initials glitch)
  const [avatarFailed, setAvatarFailed] = useState(false);
  if (!profileData) {
    return null;
  }

  const currentUserId =
    ctx?.id || ctx?.user?.id || ctx?.data?.id || ctx?.session?.user?.id || null;

  const avatarSrc =
    profileData.avatarUrl || profileData.image || '/images/demo-profile.png';

  const getInitials = (name) =>
    name
      ? name
          .split(' ')
          .map((w) => w[0])
          .join('')
          .toUpperCase()
          .slice(0, 2)
      : 'T';

  const handleMessageTeacher = () => {
    if (profileData.isOwnProfile) {
      alert("You can't message yourself.");
      return;
    }

    if (!currentUserId) {
      const next = `/teacher-profile/${profileData.id}`;
      router.push(`/login?next=${encodeURIComponent(next)}`);
      return;
    }

    if (typeof onMessageClick === 'function') {
      setTimeout(onMessageClick, 0);
    }
  };

  function isTrustedUrl(url) {
    try {
      const u = new URL(url);
      const host = u.hostname.toLowerCase();
      const allowedDomains = [
        'supabase.co',
        'penguinlearn.com',
        'cdn.penguinlearn.com',
        'localhost',
        '127.0.0.1',
      ];
      return allowedDomains.some((d) => host === d || host.endsWith(`.${d}`));
    } catch {
      return false;
    }
  }

  async function resetIntroVid(url) {
    try {
      if (url.startsWith('http') && !isTrustedUrl(url)) {
        alert('External video URLs not allowed.');
        return;
      }

      await fetch('/api/user', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          introductionVideo: url,
        }),
      });

      setUploadVidModalOpen(false);
      router.refresh();
    } catch {
      alert('Failed to upload video.');
    }
  }

  return (
    <>
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* LEFT */}
            <div className="space-y-6">
              <div className="flex items-center gap-6">
                {/* add `relative` so absolute fallback stays inside circle */}
                <div className="relative h-24 w-24 border-4 border-white shadow-lg rounded-full overflow-hidden bg-blue-100">
                  {!avatarFailed ? (
                    <img
                      src={avatarSrc}
                      alt={profileData.name}
                      className="w-full h-full object-cover"
                      onError={() => setAvatarFailed(true)}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-blue-100 flex items-center justify-center text-blue-700 text-2xl font-bold">
                      {getInitials(profileData.name)}
                    </div>
                  )}
                </div>

                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">
                    {profileData.name}
                  </h1>
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                    <Award className="h-3 w-3 mr-1" />
                    Verified Teacher
                  </div>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed">
                {profileData.description ||
                  profileData.about ||
                  'Passionate educator dedicated to helping students achieve their learning goals.'}
              </p>

              {profileData.isOwnProfile && (
                <Link
                  href="/settings"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-white/50"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Edit Profile
                </Link>
              )}

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() =>
                    document
                      .getElementById('upcoming-classes')
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                  className="inline-flex items-center px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold"
                >
                  <BookOpen className="h-5 w-5 mr-2" />
                  View Classes
                </button>

                <button
                  type="button"
                  onClick={handleMessageTeacher}
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 hover:bg-white/50 rounded-lg font-semibold"
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Message Teacher
                </button>
              </div>
            </div>

            {/* RIGHT */}
            <div className="relative">
              {profileData.isOwnProfile && !profileData.introductionVideo && (
                <div className="aspect-video bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center">
                  <p className="text-gray-600 mb-2">No introduction video yet</p>
                  <button
                    type="button"
                    onClick={() => setUploadVidModalOpen(true)}
                    className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-800"
                  >
                    Upload Video
                  </button>
                </div>
              )}

              {profileData.introductionVideo && (
                <div className="aspect-video rounded-xl overflow-hidden shadow-2xl bg-black">
                  <video
                    className="w-full h-full object-cover"
                    controls
                    muted
                    playsInline
                  >
                    <source
                      src={profileData.introductionVideo}
                      type="video/mp4"
                    />
                  </video>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoUrl={profileData.introductionVideo}
        teacherName={profileData.name}
      />

      <UploadIntroVidModal
        isOpen={uploadVidModalOpen}
        onClose={() => setUploadVidModalOpen(false)}
        onSave={resetIntroVid}
      />
    </>
  );
}